import { DebtSecurityWithConvertedValue } from "./types";

export interface IssuerGroup {
  name: string;
  total: number;
  percentage: number;
}

/**
 * Computes the total amount by summing convertedFaceValue across all securities.
 * Requirements: 2.2, 2.3, 2.4
 */
export function computeTotalAmount(
  securities: DebtSecurityWithConvertedValue[]
): number {
  return securities.reduce((sum, s) => sum + s.convertedFaceValue, 0);
}

/**
 * Formats a monetary amount using the given currency code with exactly two decimal places.
 * Requirements: 2.5
 */
export function formatDebtCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Groups securities by issuer name (falling back to "Unknown Issuer") and computes
 * each group's percentage of the total converted face value.
 * Requirements: 3.2, 3.3
 */
export function groupByIssuer(
  securities: DebtSecurityWithConvertedValue[]
): IssuerGroup[] {
  const totals = new Map<string, number>();

  for (const security of securities) {
    const name = security.issuer?.name ?? "Unknown Issuer";
    totals.set(name, (totals.get(name) ?? 0) + security.convertedFaceValue);
  }

  const grandTotal = computeTotalAmount(securities);

  return Array.from(totals.entries()).map(([name, total]) => ({
    name,
    total,
    percentage: grandTotal === 0 ? 0 : (total / grandTotal) * 100,
  }));
}
